import React, { useEffect, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from "reactstrap";
import { useForm } from "react-hook-form";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import { parseLocalDate } from "helpers/constant";
import { getUserActivityFields } from "../helper/auditconstant";
import { UserActivityFilterFormValues } from "../helper/audit.types";

interface GenerateUserActivityReportModalProps {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (values: UserActivityFilterFormValues) => void;
  isGenerating: boolean;
}

const GenerateUserActivityReportModal: React.FC<
  GenerateUserActivityReportModalProps
> = ({ isOpen, toggle, onSubmit, isGenerating }) => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<UserActivityFilterFormValues>({
    defaultValues: {
      fromdate: null,
      todate: null,
      useractivity: "All",
    },
  });

  const fromDateValue = watch("fromdate");
  const toDateValue = watch("todate");

  useEffect(() => {
    if (isOpen) {
      reset({
        fromdate: null,
        todate: null,
        useractivity: "All",
      });
    }
  }, [isOpen, reset]);

  useEffect(() => {
    if (fromDateValue && toDateValue) {
      const fromDate = parseLocalDate(fromDateValue);
      const toDate = parseLocalDate(toDateValue);
      if (fromDate && toDate && fromDate > toDate) {
        setValue("todate", null);
      }
    }
  }, [fromDateValue, toDateValue, setValue]);

  const formFields: FormFieldConfig[] = useMemo(
    () => getUserActivityFields(fromDateValue, true),
    [fromDateValue],
  );

  const handleFormSubmit = (values: any) => {
    onSubmit(values);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      backdrop="static"
      className="common_modal"
    >
      <ModalHeader toggle={toggle} className="p-3 border-bottom">
        <div className="d-flex align-items-center gap-2">
          <i className="ri-file-pdf-2-line text-danger fs-20" />
          <span className="fw-bold">Generate User Activity Report</span>
        </div>
      </ModalHeader>

      <ModalBody className="p-4">
        <div className="mb-3">
          <p className="text-muted fs-12 mb-0">
            Select date range and activity criteria to generate the report PDF.
          </p>
        </div>

        <CommonForm
          fields={formFields}
          control={control}
          errors={errors}
          columns={1}
          setValue={setValue}
          getValues={getValues}
        />
      </ModalBody>

      <ModalFooter className="bg-white border-top">
        <Button color="secondary" outline onClick={toggle} disabled={isGenerating}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit(handleFormSubmit)}
          disabled={isGenerating}
          className="d-flex align-items-center"
        >
          {isGenerating ? (
            <>
              <Spinner size="sm" className="me-2" />
              Generating...
            </>
          ) : (
            <>
              <i className="ri-file-pdf-line me-1" />
              Generate Report
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default GenerateUserActivityReportModal;
